/* eslint-disable no-console */
import { useEffect, useRef } from 'react';

/**
 * Detect HV chủ động mở DevTools qua phím tắt:
 *   - F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+U
 *
 * Đã từng có thêm 2 method auto-polling (dimension diff + regex.toString)
 * nhưng cả hai đều false-positive trên một số WebView (Zalo, FB in-app),
 * browser zoom levels, devtools docked-bottom với screen ngắn, và một số
 * console interceptors (extensions, RN debugger). False-positive →
 * callback chạy logout flow → reload loop hoặc auto-logout sau khi login.
 *
 * Trade-off khi bỏ polling: HV mở DevTools TRƯỚC khi load page (vd dùng
 * keyboard shortcut từ tab khác rồi dán URL) sẽ không bị bắt. Chấp nhận
 * — đó là use-case tinh vi hơn so với việc HV bình thường nhấn F12 trong
 * lúc đang ở trang. Backend cap-enforcer + suspicious-UA middleware là
 * lớp phòng thủ thứ hai cho trường hợp đó.
 *
 * Hook gọi `onOpen({ method, viewport })` một lần mỗi tab session (dedupe
 * qua ref). Callback có thể return false để hook không latch firedRef
 * (cho phép re-detect sau, vd HV chưa đăng nhập).
 */
const useDetectDevTools = (onOpen) => {
  const firedRef = useRef(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    let stopped = false;

    const fire = (method) => {
      if (firedRef.current || stopped) return;
      const viewport = {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        devicePixelRatio: window.devicePixelRatio,
      };
      try {
        const result = onOpenRef.current && onOpenRef.current({ method, viewport });
        if (result !== false) firedRef.current = true;
      } catch (_) {
        firedRef.current = true;
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'F12') return fire('key-F12');
      if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) return fire('key-ctrl-shift-i');
      if (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')) return fire('key-ctrl-shift-j');
      if (e.ctrlKey && e.shiftKey && (e.key === 'C' || e.key === 'c')) return fire('key-ctrl-shift-c');
      if ((e.metaKey || e.ctrlKey) && (e.key === 'U' || e.key === 'u')) return fire('key-view-source');
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      stopped = true;
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);
};

export default useDetectDevTools;
